import cn from 'classnames';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import type { AddressMatches } from 'features/my-zone/support-requests/components/support-requests-modals/types';
import type {
    ColumnsMatching,
    PropertyFormValues,
} from 'features/my-zone/support-requests/components/support-requests-modals/modals/request-new-location/request-property/types';
import {
    BasicDropdownMenu,
    type BasicMenuItemProps,
    BodyText,
    Dropdown,
    FilterTaggedButton,
    Icon,
    MenuText,
} from '@placer-ui/components';
import { Loading } from 'ui-components/loading/loading-component';
import {
    findAddressMatches,
    findNameMatch,
    findZipCodeMatch,
} from 'features/my-zone/support-requests/components/support-requests-modals/utils/columns-matcher/columns-matcher';
import { ControlledBasicDropdownMenu } from 'features/my-zone/support-requests/components/support-requests-modals/components/inputs/controlled-basic-dropdown';
import {
    csvAddressFields,
    csvAddressOptions,
    SupportRequestAddressType,
} from './file-fields-mapping-config';
import { processColumnsValidation } from 'features/my-zone/support-requests/components/support-requests-modals/utils/columns-validation/columns-validation';

import styles from './file-fields-mapping.module.scss';
import isEmpty from 'lodash/isEmpty';
import { useFormContext } from 'react-hook-form';
import { supportRequestAddressTypeStore } from 'features/my-zone/support-requests/store/property-store';
import { useSetAtom } from 'jotai';

export const FileFieldsMapping = () => {
    const formController = useFormContext<PropertyFormValues>();
    // const parsedData = useAtomValue(parsedDataStore);
    const [addressOptions, setAddressOptions] = useState(csvAddressOptions);
    const setSupportRequestAddressType = useSetAtom(supportRequestAddressTypeStore);
    const [currentColumns, setCurrentColumns] = useState<BasicMenuItemProps[] | undefined>(
        undefined,
    );
    const [emptyColumns, setEmptyColumns] = useState<string[]>([]);

    // TODO - should it be global state?
    const columnMap = formController.getValues('matching');
    const parsedData = formController.getValues('parsedData');

    const autoDetectedAddressMatches = useRef<ColumnsMatching>({});

    const selectedAddressOption =
        addressOptions.find(({ isSelected }) => isSelected) || csvAddressOptions[0];

    // TODO - think about remove useEffect
    useEffect(() => {
        if (parsedData && isEmpty(columnMap)) {
            const { fields, emptyColumns, data } = parsedData;
            const map: Record<string, string> = {};

            if (fields) {
                const fieldsForMatching = fields.filter((field) => !emptyColumns?.includes(field));
                const nameMatch = findNameMatch(fieldsForMatching);
                const addressMatches = findAddressMatches(fieldsForMatching, data);
                const zipCodeMatch = findZipCodeMatch(fieldsForMatching, data);

                if (nameMatch) {
                    map['name'] = nameMatch;
                }

                if (addressMatches) {
                    const addressType = csvAddressOptions.find(
                        (option) => option.value === addressMatches.type,
                    );
                    const relevantFields = addressType
                        ? csvAddressFields[addressType.value].map((item) => item.value)
                        : [];

                    (Object.keys(addressMatches.matches) as Array<keyof AddressMatches>).forEach(
                        (key) => {
                            const addressColumn = addressMatches.matches[key];
                            if (addressColumn) {
                                autoDetectedAddressMatches.current[key] = addressColumn;
                                if (relevantFields.includes(key)) {
                                    map[key] = addressColumn;
                                }
                            }
                        },
                    );

                    if (addressType) {
                        setAddressOptions((prev) =>
                            prev.map((option) => ({
                                ...option,
                                isSelected: option.value === addressType.value,
                            })),
                        );
                    }

                    setSupportRequestAddressType(addressType?.value || 'single_column');
                }

                if (zipCodeMatch) {
                    map['zip_code'] = zipCodeMatch;
                }

                formController.setValue('matching', map);
                setEmptyColumns(emptyColumns || []);
                setCurrentColumns(
                    processColumnsValidation(fields, data, Object.values(map), emptyColumns || []),
                );

                formController.trigger();
            }
        }
    }, [parsedData, formController, columnMap, setSupportRequestAddressType]);

    const handleSelection = useCallback(
        (selectedColumn: BasicMenuItemProps | undefined, prevColumnLabel?: string) => {
            setCurrentColumns((prev) => {
                if (!prev) return prev;

                const selectedValues = [
                    ...Object.values(columnMap).filter((val) => val !== prevColumnLabel),
                    ...(selectedColumn ? [selectedColumn.value] : []),
                ];

                return processColumnsValidation(
                    prev.map((item) => item.value),
                    parsedData!.data,
                    selectedValues,
                    emptyColumns,
                );
            });

            formController.trigger();
        },
        [columnMap, emptyColumns, formController, parsedData],
    );
    const selectAddressType = useCallback(
        (type: BasicMenuItemProps<SupportRequestAddressType>) => {
            if (!selectedAddressOption || type.value === selectedAddressOption.value) return;

            const currentUserSelections = { ...columnMap };

            setAddressOptions((prev) =>
                prev.map((option) => ({
                    ...option,
                    isSelected: option.value === type.value,
                })),
            );

            setSupportRequestAddressType(type.value);

            const relevantFields = csvAddressFields[type.value].map((item) => item.value);

            const newColumnMap: ColumnsMatching = {
                name: currentUserSelections.name,
                zip_code: currentUserSelections.zip_code,
            };

            relevantFields.forEach((field) => {
                if (field in currentUserSelections) {
                    newColumnMap[field] = currentUserSelections[field];
                } else if (autoDetectedAddressMatches.current[field]) {
                    newColumnMap[field] = autoDetectedAddressMatches.current[field];
                } else {
                    newColumnMap[field] = '';
                }
            });

            Object.entries(currentUserSelections).forEach(([key, value]) => {
                if (key !== 'name' && key !== 'zip_code' && !relevantFields.includes(key)) {
                    autoDetectedAddressMatches.current[key] = value;
                }
            });

            formController.setValue('matching', newColumnMap);
            formController.trigger('matching');

            const selectedValues = Object.values(newColumnMap).filter(Boolean);

            setCurrentColumns((prev) =>
                prev
                    ? processColumnsValidation(
                          prev.map((item) => item.value),
                          parsedData!.data,
                          selectedValues,
                          emptyColumns,
                      )
                    : prev,
            );

            formController.trigger();
        },
        [
            columnMap,
            emptyColumns,
            formController,
            parsedData,
            selectedAddressOption,
            setSupportRequestAddressType,
        ],
    );

    const ColumnSelector = ({ column }: { column: BasicMenuItemProps }) => {
        const [selectedColumn, setSelectedColumn] = useState<string | undefined>(
            columnMap[column.value],
        );

        useEffect(() => {
            const isSelected = currentColumns?.find(
                (item) => item.value === selectedColumn,
            )?.isDisabled;

            if (!isSelected) {
                setSelectedColumn(undefined);
            }
        }, [selectedColumn]);

        const onItemSelection = (item: BasicMenuItemProps | undefined) => {
            setSelectedColumn(item?.value);
            handleSelection(item, selectedColumn);

            const updatedMap = { ...formController.getValues('matching') };
            updatedMap[column.value] = item?.value || '';
            formController.setValue('matching', updatedMap);
        };

        const onClearClick = () => {
            onItemSelection(undefined);
        };

        if (!currentColumns) return null;

        return (
            <>
                <div className={styles.disabledSelectBox}>
                    <BodyText variant="semi-bold" testId={`match-csv-label-${column.value}`}>
                        {column.label}
                    </BodyText>
                </div>
                <Icon type="ArrowRight" disabled weight="outline" />
                <div className={styles.singleSelectBox} data-testid="match-csv-selector-container">
                    <ControlledBasicDropdownMenu
                        name={`matching.${column.value}`}
                        testId={`match-csv-selector-${column.value}`}
                        control={formController.control}
                        items={currentColumns}
                        placeholder="Select Column"
                        onItemSelection={onItemSelection}
                        defaultValue={selectedColumn}
                        required={column.value !== 'zip_code'}
                        onClearClick={onClearClick}
                        showClearIndicator
                    />
                </div>
            </>
        );
    };

    return (
        <>
            <div className={styles.content}>
                <Loading show={!currentColumns} className={styles.loader}>
                    <div className={styles.gridHeader}>
                        <MenuText color="secondary" variant="semi-bold">
                            Placer’s Required Data
                        </MenuText>
                        <br />
                        <MenuText color="secondary" variant="semi-bold">
                            File Column
                        </MenuText>
                    </div>
                    <div className={styles.gridBlock}>
                        <ColumnSelector column={{ label: 'Name', value: 'name' }} />
                    </div>
                    <Dropdown
                        trigger={
                            <FilterTaggedButton
                                categoryText="Address: "
                                tagValue={String(selectedAddressOption?.label)}
                                testId="support-request-match-csv-address"
                            />
                        }
                        scaleToAnchor
                        appendToParent
                        closeOnSelection
                    >
                        <BasicDropdownMenu
                            items={addressOptions}
                            onItemSelection={selectAddressType}
                        />
                    </Dropdown>
                    <div className={cn(styles.gridBlock, styles.extraMargin)}>
                        {selectedAddressOption &&
                            csvAddressFields[selectedAddressOption.value].map((item) => (
                                <ColumnSelector column={item} key={item.value} />
                            ))}
                    </div>
                    <ColumnSelector column={{ label: 'Zip Code (optional)', value: 'zip_code' }} />
                </Loading>
            </div>
        </>
    );
};
